import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApiAdmintypologiesPost } from '@r/api/generated/client.ts';
import { toast } from 'sonner';
import type { FC, FormEvent } from 'react';
import type {
  TypologyTypologyReadModel,
  TypologyTypologyWriteModel
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { Switch } from '@ui/switch.tsx';
import { Trans } from 'react-i18next';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type Typology = TypologyTypologyReadModel;

interface TypologyFormProps {
  onSubmitSuccess: (typology: Typology) => void | Promise<void>;
  useLocalState?: boolean;
  typologies?: Partial<Typology>[];
}

export const TypologyForm: FC<TypologyFormProps> = ({
  onSubmitSuccess,
  useLocalState = false,
  typologies
}) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const typologiesHasDefault = typologies?.some((typology) => typology.isDefault) ?? false;

  const FormSchema = z.object({
    name: z
      .string()
      .min(3, t('form.error.char.min', { minChar: 3 }))
      .max(100, t('form.error.char.max', { maxChar: 100 }))
      .describe(t('form.schema.describe.name')),
    pointCoeff: z.coerce.number().min(0).describe(t('admin.typology.schema.describe.point.coeff')),
    isDefault: z.boolean()
  });

  type FormValues = z.infer<typeof FormSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: '',
      pointCoeff: 1,
      isDefault: false
    }
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    if (useLocalState) {
      const mock = createMockTypology(data);
      void onSubmitSuccess(mock);
      toast.success(t('admin.typology.creation.success'));
      form.reset();
      return;
    }
    const body: TypologyTypologyWriteModel = {
      name: data.name,
      pointCoeff: data.pointCoeff,
      isDefault: data.isDefault
    };
    typologyMutation.mutate({ data: body });
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit(onSubmit)(e);
  };

  const createMockTypology = (formValues: FormValues): Typology => ({
    id: Date.now(),
    name: formValues.name,
    pointCoeff: formValues.pointCoeff,
    isDefault: formValues.isDefault,
    createdAt: new Date().toISOString()
  });

  const typologyMutation = useApiAdmintypologiesPost({
    mutation: {
      onSuccess: async (result: Typology) => {
        await onSubmitSuccess(result);
        toast.success(t('admin.typology.creation.success'));
        form.reset();
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'post', '/admin/typologies')) {
          showOrvalErrorToast(error, t('admin.typology.creation.error'));
        } else {
          toast.error(t('admin.typology.creation.error'));
        }
      },
      networkMode: 'always'
    }
  });

  const dynamicPointsName = t('point.name_other');
  const mutedText = t('admin.typology.coeff.description.text.muted', {
    pointsName: dynamicPointsName
  });

  return (
    <Form {...form} schema={FormSchema}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.typology.name.form.input.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('admin.typology.name.form.input.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="pointCoeff"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.typology.coeff.form.input.label')}</FormLabel>
              <ul className="flex flex-col gap-3 pl-5 text-sm list-disc text-start text-foreground">
                <li>
                  <Trans
                    i18nKey="admin.typology.coeff.description.text1"
                    components={{
                      strong: <strong />
                    }}
                    values={{ pointsName: dynamicPointsName }}
                  />
                </li>
                <li>
                  <Trans
                    i18nKey="admin.typology.coeff.description.text2"
                    components={{
                      strong: <strong />
                    }}
                    values={{ pointsName: dynamicPointsName }}
                  />
                </li>
                <span className="text-muted-foreground">{mutedText}</span>
              </ul>
              <FormControl>
                <Input
                  placeholder={t('admin.typology.coeff.form.input.placeholder')}
                  type="number"
                  min={0}
                  step={0.001}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isDefault"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('admin.typology.default.form.input.label')}</FormLabel>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={typologiesHasDefault}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          rounded="full"
          loading={typologyMutation.isPending}
          className="w-full"
          type="submit"
        >
          {t('admin.typology.creation.form.button')}
        </Button>
      </form>
    </Form>
  );
};
